import { useEffect, useSyncExternalStore } from 'react';

// Powiadomienia Web Push (braki, propozycje zamienników, pozycje dopisane w trakcie zakupów).
// Subskrypcja należy do przeglądarki; serwer trzyma jej kopię przypisaną do zalogowanej osoby.

export type PushSupport = 'unsupported' | 'denied' | 'default' | 'granted';

const SW_READY_TIMEOUT_MS = 10_000;
const SERVER_DISABLED = 'Powiadomienia są wyłączone na serwerze.';

const browserSupports = () =>
  typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

/** Serwer bez kluczy VAPID (GET /api/push/key → publicKey: null). */
let serverDisabled = false;

/** 'unsupported' także wtedy, gdy powiadomienia są wyłączone na serwerze (wiadomo po pierwszym sprawdzeniu). */
export function pushSupport(): PushSupport {
  if (!browserSupports() || serverDisabled) return 'unsupported';
  return Notification.permission;
}

let enabled: boolean | undefined;
let checking: Promise<void> | null = null;
const listeners = new Set<() => void>();

function notify() {
  for (const fn of listeners) fn();
}

function subscribeStore(fn: () => void) {
  listeners.add(fn);
  return () => void listeners.delete(fn);
}

function setEnabled(next: boolean) {
  if (enabled === next) return;
  enabled = next;
  notify();
}

async function registration(): Promise<ServiceWorkerRegistration | undefined> {
  if (!browserSupports()) return undefined;
  return navigator.serviceWorker.getRegistration();
}

async function currentSubscription(): Promise<PushSubscription | null> {
  return (await (await registration())?.pushManager.getSubscription()) ?? null;
}

function check(): Promise<void> {
  if (browserSupports()) void fetchKey().catch(() => {});
  checking ??= currentSubscription()
    .then((sub) => setEnabled(!!sub && Notification.permission === 'granted'))
    .catch(() => setEnabled(false))
    .finally(() => {
      checking = null;
    });
  return checking;
}

/** Czy to urządzenie ma aktywną subskrypcję powiadomień (undefined = jeszcze sprawdzamy). */
export function usePushEnabled(): boolean | undefined {
  const value = useSyncExternalStore(subscribeStore, () => enabled);
  useEffect(() => {
    void check();
  }, []);
  return value;
}

/** pushSupport() odświeżane po sprawdzeniu serwera i po prośbie o zgodę. */
export function usePushSupport(): PushSupport {
  const value = useSyncExternalStore(subscribeStore, pushSupport);
  useEffect(() => {
    void check();
  }, []);
  return value;
}

/** Prosi o zgodę, subskrybuje i zgłasza subskrypcję serwerowi. Błędy jako Error z polskim komunikatem. */
export async function enablePush(): Promise<void> {
  if (!browserSupports()) {
    throw new Error('Ta przeglądarka nie obsługuje powiadomień. Na iPhonie dodaj Odhaczkę do ekranu początkowego.');
  }
  if (serverDisabled) throw new Error(SERVER_DISABLED);
  const permission = await Notification.requestPermission();
  notify();
  if (permission !== 'granted') {
    throw new Error('Brak zgody na powiadomienia. Można ją włączyć w ustawieniach przeglądarki.');
  }
  const reg = await readyRegistration();
  const key = await serverKey();
  const sub = await subscribe(reg, key);
  try {
    await postSubscription(sub);
  } catch (e) {
    // Bez kopii na serwerze subskrypcja jest bezużyteczna — nie udajemy, że powiadomienia działają.
    await sub.unsubscribe().catch(() => {});
    setEnabled(false);
    throw e;
  }
  setEnabled(true);
}

/** Wyłącza powiadomienia na tym urządzeniu (serwer i przeglądarka). */
export async function disablePush(): Promise<void> {
  const sub = await currentSubscription().catch(() => null);
  if (sub) {
    // Gdy serwer jest nieosiągalny, wystarczy wypisanie w przeglądarce: usługa push odpowie 410 i serwer usunie wpis.
    await fetch('/api/push/subscription', {
      method: 'DELETE',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ endpoint: sub.endpoint }),
    }).catch(() => {});
    await sub.unsubscribe().catch(() => {});
  }
  setEnabled(false);
}

/**
 * Przy starcie aplikacji (zalogowany): ponownie zgłasza istniejącą subskrypcję serwerowi,
 * a po zmianie klucza VAPID subskrybuje od nowa. Bez subskrypcji nic nie robi.
 */
export async function syncPushSubscription(): Promise<void> {
  try {
    const reg = await registration();
    const sub = await reg?.pushManager.getSubscription();
    if (!reg || !sub) {
      setEnabled(false);
      return;
    }
    if (Notification.permission !== 'granted') {
      await disablePush();
      return;
    }
    await postSubscription(await subscribe(reg, await serverKey()));
    setEnabled(true);
  } catch {
    // offline albo serwer bez VAPID — spróbujemy przy następnym starcie
  }
}

async function readyRegistration(): Promise<ServiceWorkerRegistration> {
  const timeout = new Promise<never>((_, reject) =>
    setTimeout(() => reject(new Error('Powiadomienia działają w zainstalowanej aplikacji — odśwież stronę i spróbuj ponownie.')), SW_READY_TIMEOUT_MS),
  );
  return Promise.race([navigator.serviceWorker.ready, timeout]);
}

let keyRequest: Promise<Uint8Array<ArrayBuffer> | null> | null = null;

/** Klucz VAPID serwera (null = push wyłączony na serwerze); pobierany raz na uruchomienie. */
function fetchKey(): Promise<Uint8Array<ArrayBuffer> | null> {
  keyRequest ??= (async () => {
    let res: Response;
    try {
      res = await fetch('/api/push/key', { credentials: 'same-origin' });
    } catch {
      throw new Error('Brak połączenia z internetem — powiadomienia można włączyć online.');
    }
    // 503 push_disabled albo publicKey: null — serwer bez kluczy VAPID.
    if (!res.ok && res.status !== 503) throw new Error('Powiadomienia są chwilowo niedostępne na serwerze.');
    const publicKey = res.ok ? ((await res.json()) as { publicKey: string | null }).publicKey : null;
    if (!publicKey) {
      serverDisabled = true;
      notify();
      return null;
    }
    return base64UrlToBytes(publicKey);
  })().catch((e: unknown) => {
    keyRequest = null;
    throw e;
  });
  return keyRequest;
}

async function serverKey(): Promise<Uint8Array<ArrayBuffer>> {
  const key = await fetchKey();
  if (!key) throw new Error(SERVER_DISABLED);
  return key;
}

async function subscribe(reg: ServiceWorkerRegistration, key: Uint8Array<ArrayBuffer>): Promise<PushSubscription> {
  const existing = await reg.pushManager.getSubscription();
  if (existing) {
    const current = existing.options.applicationServerKey;
    if (current && sameBytes(new Uint8Array(current), key)) return existing;
    // Subskrypcja z innym kluczem VAPID — przeglądarka nie pozwoli na drugą.
    await existing.unsubscribe();
  }
  try {
    return await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
  } catch {
    throw new Error('Nie udało się włączyć powiadomień w tej przeglądarce.');
  }
}

async function postSubscription(sub: PushSubscription): Promise<void> {
  const json = sub.toJSON();
  let res: Response;
  try {
    res = await fetch('/api/push/subscription', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ endpoint: json.endpoint, keys: { p256dh: json.keys?.p256dh, auth: json.keys?.auth } }),
    });
  } catch {
    throw new Error('Brak połączenia z internetem — powiadomienia można włączyć online.');
  }
  if (!res.ok) throw new Error('Serwer nie przyjął subskrypcji powiadomień.');
}

function base64UrlToBytes(value: string): Uint8Array<ArrayBuffer> {
  const b64 = (value + '='.repeat((4 - (value.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function sameBytes(a: Uint8Array, b: Uint8Array): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}
